import { useTranslation } from "react-i18next";
import "../../../i18n";
import SectionLabel from "../components/SectionLabel";
import EvalCard from "../components/EvalCard";

function EvaluationSection() {
  const { t } = useTranslation("marketing");

  const mathCard = {
    urlPath: "/evaluate/mathematics-cl9",
    monogram: "M",
    tileStyle: { background: "rgba(59,130,246,0.15)", color: "#60A5FA" },
    subjectName: t("evaluation.cards.math.subject"),
    subjectMeta: t("evaluation.cards.math.meta"),
    score: "9/10",
    question: t("evaluation.cards.math.question"),
    answer: t("evaluation.cards.math.answer"),
    steps: [
      { ok: true, text: t("evaluation.cards.math.step1") },
      { ok: true, text: t("evaluation.cards.math.step2") },
      { ok: false, text: t("evaluation.cards.math.step3"), note: t("evaluation.cards.math.step3Note") },
      { ok: true, text: t("evaluation.cards.math.step4") },
    ],
    tip: t("evaluation.cards.math.tip"),
  };

  const biologyCard = {
    urlPath: "/evaluate/biology-cl11",
    monogram: "B",
    tileStyle: { background: "rgba(34,197,94,0.15)", color: "#4ADE80" },
    subjectName: t("evaluation.cards.biology.subject"),
    subjectMeta: t("evaluation.cards.biology.meta"),
    score: "16/20",
    question: t("evaluation.cards.biology.question"),
    answer: t("evaluation.cards.biology.answer"),
    criteria: [
      { label: t("evaluation.cards.biology.criteria1"), widthPct: 100, score: "8/8", fill: "green" },
      { label: t("evaluation.cards.biology.criteria2"), widthPct: 80, score: "4/5", fill: "green" },
      { label: t("evaluation.cards.biology.criteria3"), widthPct: 57, score: "4/7", fill: "orange" },
    ],
    tip: t("evaluation.cards.biology.tip"),
  };

  const physicsCard = {
    urlPath: "/evaluate/physics-cl12",
    monogram: "P",
    tileStyle: { background: "rgba(234,179,8,0.15)", color: "#FCD34D" },
    subjectName: t("evaluation.cards.physics.subject"),
    subjectMeta: t("evaluation.cards.physics.meta"),
    score: "15/20",
    question: t("evaluation.cards.physics.question"),
    answer: t("evaluation.cards.physics.answer"),
    criteria: [
      { label: t("evaluation.cards.physics.criteria1"), widthPct: 80, score: "8/10", fill: "green" },
      { label: t("evaluation.cards.physics.criteria2"), widthPct: 100, score: "5/5", fill: "green" },
      { label: t("evaluation.cards.physics.criteria3"), widthPct: 40, score: "2/5", fill: "red" },
    ],
    tip: t("evaluation.cards.physics.tip"),
  };

  const chemistryCard = {
    urlPath: "/evaluate/chemistry-cl12",
    monogram: "C",
    tileStyle: { background: "rgba(168,85,247,0.15)", color: "#C084FC" },
    subjectName: t("evaluation.cards.chemistry.subject"),
    subjectMeta: t("evaluation.cards.chemistry.meta"),
    score: "17/20",
    question: t("evaluation.cards.chemistry.question"),
    answer: t("evaluation.cards.chemistry.answer"),
    criteria: [
      { label: t("evaluation.cards.chemistry.criteria1"), widthPct: 90, score: "9/10", fill: "green" },
      { label: t("evaluation.cards.chemistry.criteria2"), widthPct: 80, score: "4/5", fill: "green" },
      { label: t("evaluation.cards.chemistry.criteria3"), widthPct: 80, score: "4/5", fill: "orange" },
    ],
    tip: t("evaluation.cards.chemistry.tip"),
  };

  const yes = <><span className="ul-tick">✓</span> {t("evaluation.table.yes")}</>;
  const notAvailable = <><span className="ul-cross">✗</span> {t("evaluation.table.notAvailable")}</>;
  const compareRows = [
    { cap: t("evaluation.table.cap1"), other: yes, markszen: yes },
    { cap: <strong style={{ color: "white" }}>{t("evaluation.table.cap2")}</strong>, other: notAvailable, markszen: t("evaluation.table.mz2") },
    { cap: <strong style={{ color: "white" }}>{t("evaluation.table.cap3")}</strong>, other: notAvailable, markszen: t("evaluation.table.mz3") },
    { cap: t("evaluation.table.cap4"), other: notAvailable, markszen: t("evaluation.table.mz4") },
    { cap: t("evaluation.table.cap5"), other: notAvailable, markszen: t("evaluation.table.mz5") },
    { cap: t("evaluation.table.cap6"), other: t("evaluation.table.partial"), markszen: t("evaluation.table.mz6") },
  ];

  return (
    <section className="ul-section ul-usp-section" id="evaluation">
      <div className="ul-center" style={{ marginBottom: 52, position: "relative", zIndex: 1 }}>
        <SectionLabel>{t("evaluation.label")}</SectionLabel>
        <h2 className="ul-section-title">
          {t("evaluation.titleLine1")}<br />{t("evaluation.titleLine2")}
        </h2>
        <p className="ul-section-sub">
          {t("evaluation.sub")}
        </p>
      </div>

      <div className="ul-eval-grid">
        <EvalCard {...mathCard} />
        <EvalCard {...biologyCard} />
        <EvalCard {...physicsCard} />
        <EvalCard {...chemistryCard} />
      </div>

      <div className="ul-compare-wrap">
        <div className="ul-center" style={{ marginBottom: 24 }}>
          <SectionLabel style={{ margin: "0 auto 12px", background: "rgba(249,115,22,0.1)", borderColor: "rgba(249,115,22,0.2)" }}>
            {t("evaluation.compareLabel")}
          </SectionLabel>
          <h3 style={{ fontSize: 28, fontWeight: 800, color: "white", letterSpacing: "-1px" }}>
            {t("evaluation.compareTitle")}
          </h3>
        </div>
        <div className="ul-compare-table-wrap">
          <table className="ul-compare-table">
            <thead>
              <tr>
                <th>{t("evaluation.table.capability")}</th>
                <th>{t("evaluation.table.otherTools")}</th>
                <th>{t("evaluation.table.markszen")}</th>
              </tr>
            </thead>
            <tbody>
              {compareRows.map((row, i) => (
                <tr key={i}>
                  <td>{row.cap}</td>
                  <td>{row.other}</td>
                  <td>{row.markszen}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default EvaluationSection;
